import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Grid,
    Snackbar,
    Alert as MuiAlert,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllCmsContents } from "../../Apis/CmsContentApi";

const CmsContentList = () => {
    const [contents, setContents] = useState([]);
    const [filteredContents, setFilteredContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [searchType, setSearchType] = useState("Privacy Policy"); // Initial selection
    const navigate = useNavigate();

    const allowedContentTypes = ["Terms and Conditions", "Privacy Policy"];

    const fetchContents = async () => {
        try {
            setLoading(true);
            const data = await getAllCmsContents();
            const allContents = data?.content || [];
            setContents(allContents);

            // Filter initially by "Privacy Policy"
            const initialFiltered = allContents.filter(content => content.contentType === "Privacy Policy");
            setFilteredContents(initialFiltered);

            setError(null);
        } catch {
            setError("Failed to fetch CMS contents.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContents();
    }, []);

    // Handle dropdown change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchType(value);
        if (value === "") {
            setFilteredContents(contents);
        } else {
            const filtered = contents.filter(content =>
                content.contentType === value
            );
            setFilteredContents(filtered);
        }
    };

    return (
        <Box sx={{ padding: 3, width: "95%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">CMS Contents</Typography>
            </Box>

            {/* Dropdown Filter */}
            <Box sx={{ mb: 3, width: '300px' }}>
                <FormControl fullWidth variant="outlined">

                    <InputLabel>Content Type</InputLabel>
                    <Select
                        value={searchType}
                        label="Content Type"
                        onChange={handleSearchChange}
                        sx={{ height: 40 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {allowedContentTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box sx={{ textAlign: "center", mb: 2 }}>
                    <MuiAlert severity="error">{error}</MuiAlert>
                </Box>
            )}

            {!loading && filteredContents.length === 0 && (
                <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
                    No contents found.
                </Typography>
            )}

            {!loading && filteredContents.length > 0 && (
                <Grid container spacing={4}>
                    {filteredContents.map((content) => (
                        <Grid item xs={12} sm={8} md={12} key={content.contentType} sx={{ width: "80%", mx: "auto" }}>
                            <Card
                                elevation={4}
                                sx={{
                                    // height: "auto",
                                    width: "100%",
                                    position: "relative", // allows absolute positioning
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: 2,
                                }}
                            >
                                <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                                    <Button
                                        size="medium"
                                        sx={{ height: 40, width: 150 }}
                                        variant="contained"
                                        onClick={() => navigate(`/cmscontent/edit/${content.contentType}`)}
                                    >
                                        Edit
                                    </Button>
                                </Box>

                                <CardContent sx={{ flexGrow: 1, mt: 4 }}>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        {content.contentType}
                                    </Typography>
                                    <Typography variant="body1" >
                                        <div dangerouslySetInnerHTML={{ __html: content.descriptions }} />
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
            {/* Snackbar Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default CmsContentList;
